import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  Box, Flex, Text, Button, Checkbox, Badge, Table, Tbody, Tr, Td,
  useToast,
} from "@chakra-ui/react";
import { supabase } from "../lib/supabaseClient";
import { isNativePlatform } from "../lib/platformUtils";
import {
  isWithinGrace, isImpersonating, hashDocumentText,
} from "../lib/policyConsentRules";
import { SHORTFORM_POLICY, canonicalPolicyText } from "../lib/policyDocuments";
import { tt } from "../i18n/tt";
import MarkedText from "./MarkedText";

// ============================================================================
// 숏폼 수익 배분 정책 — 차단형 동의 팝업 (6단계)
// 기획: docs/ops/policy-consent-plan.md §3·§6 / 문안: 안내 팝업 목업과 동일
//
// 차단형 규칙:
//  * 닫기 버튼·바깥 클릭·ESC 로 닫을 수 없다 (동의/거절/나중에 중 택일)
//  * Android 하드웨어 뒤로가기도 무시한다 (Capacitor backButton 리스너)
//  * 장별로 끝까지 스크롤해야 [다음] 이 활성화된다
//  * 어드민 임퍼소네이션 중에는 동의·거절 버튼을 비활성한다 (§6-6)
// ============================================================================

const TEAL = "brand.500";
const CREATOR = "#e8794a";

const Bar = ({ laeebly, creatorLabel, laeeblyLabel, soft }) => (
  <Flex borderRadius="8px" overflow="hidden" fontSize="sm" fontWeight="700" color="white" gap="2px">
    {laeebly > 0 && (
      <Flex bg={TEAL} px={3} py={2.5} align="center" w={`${laeebly}%`} minW="fit-content">
        {laeeblyLabel}
      </Flex>
    )}
    <Flex bg={soft ? "#f3b294" : CREATOR} px={3} py={2.5} align="center" justify="flex-end" flex={1}>
      {creatorLabel}
    </Flex>
  </Flex>
);

const InfoNote = ({ children }) => (
  <Flex gap={3} border="1px solid" borderColor="neutral.200" borderLeft="4px solid"
    borderLeftColor={TEAL} borderRadius="10px" bg="neutral.50" p={4} fontSize="sm" mt={4}>
    <Text>{children}</Text>
  </Flex>
);

const StepTag = ({ children }) => (
  <Badge bg="neutral.100" color="neutral.600" borderRadius="full" px={4} py={1.5} fontSize="xs" mb={4}>
    ● {children}
  </Badge>
);

// ----- 장별 본문 (문안은 목업·기획 확정본과 동일해야 한다) ---------------------
const Step1 = () => (
  <Box>
    <StepTag>{tt("a.e51d7743", "배경")}</StepTag>
    <Box borderRadius="12px" p={5} bgGradient="linear(120deg, #eef7ef, #fbf7dd)" mb={5}>
      <Text fontSize="sm" fontWeight="700">{tt("a.097a0a72", "네이버 클립 · 카카오톡 숏폼")}</Text>
      <Text fontSize="lg" fontWeight="800" my={2}>
        {tt("a.232f71e8", "두 플랫폼의")} <Text as="span" color="brand.600">{tt("a.4df85cc8", "수익 배분 방식")}</Text>{tt("a.3ec12b3a", "이 달라집니다")}
      </Text>
      <Box borderTop="1px dashed" borderColor="#cdc7ae" pt={3} fontSize="sm">
        <b>{tt("a.dfeda584", "유튜브는 해당하지 않습니다.")}</b> {tt("a.b1f84e52", "유튜브 협업 조건은 지금과 동일합니다.")}
      </Box>
    </Box>
    <Text fontSize="sm" mb={4}>
      {tt("a.1acc785d", "네이버 클립과 카카오톡 숏폼 채널들은")} <b>{tt("a.c76d0655", "다음과 같은 협력 하에")}</b> {tt("a.a1b6a10d", "수익 창출 채널이 되었습니다.")}
    </Text>
    {[
      ["크리에이터", "기획하고 편집하고 꾸준히 올리는 크리에이터님의 역량으로 조회수와 구독자가 쌓였습니다."],
      ["작품 권리사", "수익이 나기 전부터 자신의 작품을 숏폼에 쓰도록 내어주었습니다."],
      ["레이블리", "권리사, 네이버와 카카오로부터 협력을 이끌어냈습니다. 협약 체결, 작품 제공, 적법 사용에 대한 공증, 채널 운영 지원을 제공했습니다."],
    ].map(([who, desc], i) => (
      <Box key={who} border="1px solid" borderColor="neutral.200" borderRadius="12px" p={4} mt={i ? 2 : 0} fontSize="sm">
        <b>{who}</b> — {desc}
      </Box>
    ))}
    <Box border="1.5px solid" borderColor="brand.200" bg="brand.50" borderRadius="12px" textAlign="center" p={4} my={5}>
      <Text fontWeight="800" color="brand.600">{tt("a.0ada81bb", "그렇게 수익창출 채널이 되었습니다")}</Text>
      <Text fontSize="sm" color="brand.500">{tt("a.097a0a72", "네이버 클립 · 카카오톡 숏폼")}</Text>
    </Box>
    <Text fontSize="sm">
      {tt("a.5dfa2672", "그리고 이 관계는")} <b>{tt("a.b679e1b4", "지금도 이어집니다.")}</b> {tt("a.8f5d1691", "채널이 잘 될수록 세 쪽에 돌아가는 몫이 함께 늘어나기 때문에, 작품 제공과 플랫폼 협의, 운영 지원을 계속할 이유가 생깁니다.")}
    </Text>
    <InfoNote>
      <b>{tt("a.5070a973", "모든 채널이 대상은 아닙니다.")}</b> {tt("a.f7734357", "레이블리의 지원으로 숏폼 수익창출 자격을 얻은 채널에만 적용됩니다. 직접 수익창출을 통과하신 채널은 지금과 같습니다.")}
    </InfoNote>
  </Box>
);

const Step2 = () => (
  <Box>
    <StepTag>{tt("a.ecd899b5", "협약 내용")}</StepTag>
    <Text fontSize="lg" fontWeight="800" mb={3}>{tt("a.4b2f306b", "협약에는 한 가지 조건이 있었습니다")}</Text>
    <Text fontSize="sm" mb={4}>
      {tt("a.9bb934d7", "레이블리가 제공한 작품을")} <b>{tt("a.426cc19c", "월 게시 편수의 50% 이상")}</b> {tt("a.acf1f487", "사용하는 것입니다. 나머지 절반은 크리에이터님이 자유롭게 채우실 수 있습니다.")}
    </Text>
    <Box border="1px solid" borderColor="neutral.200" borderRadius="12px" overflow="hidden" mb={4}>
      <Box p={3.5} borderBottom="1px solid" borderColor="neutral.100" fontWeight="700" fontSize="sm">
        {tt("a.c380a9eb", "협약상 월 게시 구성")}
      </Box>
      <Box p={4}>
        <Flex borderRadius="8px" overflow="hidden" gap="2px">
          <Flex bg={TEAL} color="white" px={3} py={2.5} fontSize="sm" fontWeight="700" w="50%">
            {tt("a.369cf5d5", "레이블리 제공 작품 50% 이상")}
          </Flex>
          <Box bg="neutral.200" flex={1} />
        </Flex>
        <Text fontSize="xs" color="neutral.400" mt={2}>{tt("a.6fb1a745", "나머지 절반은 자유롭게 채우실 수 있습니다")}</Text>
      </Box>
    </Box>
    <InfoNote>
      {tt("a.7280d0fb", "다만 최근 들어")} <b>{tt("a.60a0b0a5", "이 협약이 이행되지 않는 채널이 늘고 있습니다.")}</b> {tt("a.a923bb87", "레이블리 작품의 비중이 줄면, 함께 채널을 연 권리사와 레이블리에게는 돌아가는 몫이 없어집니다.")}
    </InfoNote>
    <Text fontSize="sm" mt={4}>
      {tt("a.b0a5cd5b", "이번 조정은 그 균형을 다시 맞추기 위한 것입니다.")}{" "}
      <Text as="span" color="brand.600" fontWeight="700">{tt("a.6abf71e6", "협약대로 운영해 오신 분께는 달라지는 것이 거의 없습니다.")}</Text>
    </Text>
  </Box>
);

const Step3 = () => (
  <Box>
    <StepTag>{tt("a.8f9ca943", "지금의 구조")}</StepTag>
    <Text fontSize="lg" fontWeight="800" mb={3}>{tt("a.1638330e", "수익 배분은 작품의 출처로 나뉩니다")}</Text>
    <Text fontSize="sm" mb={4}>{tt("a.ef35b1a2", "배분율은 그 영상에")} <b>{tt("a.8416acb9", "어디서 온 작품을 썼는지")}</b>{tt("a.ec4f07b4", "에 따라 세 갈래로 정해집니다.")}</Text>
    <Box border="1px solid" borderColor="neutral.200" borderRadius="12px" p={4}>
      <Flex justify="space-between" mb={1}><Text fontWeight="800" fontSize="sm">{tt("a.406e04fb", "① 클립 전용 작품")}</Text><Text fontSize="xs" color="neutral.400">{tt("a.5598ac6d", "그대로")}</Text></Flex>
      <Text fontSize="xs" color="neutral.500" mb={3}>{tt("a.43639d3d", "레이블리가 숏폼용으로 제공하는 전용 작품")}</Text>
      <Bar laeebly={40} laeeblyLabel="40" creatorLabel="60" />
    </Box>
    <Box border="1px solid" borderColor="neutral.200" borderRadius="12px" p={4} mt={3}>
      <Flex justify="space-between" mb={1}><Text fontWeight="800" fontSize="sm">{tt("a.fa6da46a", "② 레이블리 제공 작품")}</Text><Text fontSize="xs" color="neutral.400">{tt("a.5598ac6d", "그대로")}</Text></Flex>
      <Text fontSize="xs" color="neutral.500" mb={3}>{tt("a.19cace02", "일반 협업으로 제공되는 권리사 작품")}</Text>
      <Box border="1px dashed" borderColor="neutral.300" borderRadius="8px" p={2.5} textAlign="center" fontSize="sm" color="neutral.500" bg="neutral.50">
        {tt("a.5b3e0b92", "작품마다 다름 — 제공 시 미리 안내")}
      </Box>
      <Text fontSize="xs" color="neutral.500" mt={2}>{tt("a.9a935039", "미리 안내가 없었던 작품은 레이블리 60 : 크리에이터 40 이 적용됩니다.")}</Text>
    </Box>
    <Box border="1.5px solid" borderColor="brand.200" bgGradient="linear(180deg, #f2faf7, white)" borderRadius="12px" p={4} mt={3}>
      <Flex justify="space-between" mb={1}>
        <Text fontWeight="800" fontSize="sm">{tt("a.420e55b5", "③ 직접 구한 작품")}</Text>
        <Badge colorScheme="brand" fontSize="xs">{tt("a.7623120a", "이번 변경")}</Badge>
      </Flex>
      <Text fontSize="xs" color="neutral.500" mb={3}>{tt("a.2de5e087", "크리에이터님이 직접 구했거나, 위 둘에 해당하지 않는 작품")}</Text>
      <Bar laeebly={0} creatorLabel="100" />
    </Box>
    <Flex gap={5} fontSize="xs" color="neutral.500" mt={3}>
      <Flex align="center" gap={1.5}><Box w="10px" h="10px" borderRadius="3px" bg={TEAL} />{tt("a.734f3bfb", "레이블리")}</Flex>
      <Flex align="center" gap={1.5}><Box w="10px" h="10px" borderRadius="3px" bg={CREATOR} />{tt("a.567c11be", "크리에이터")}</Flex>
    </Flex>
  </Box>
);

const Step4 = () => (
  <Box>
    <StepTag>{tt("a.72af7d18", "바뀌는 것")}</StepTag>
    <Text fontSize="lg" fontWeight="800" mb={3}>{tt("a.ce35dbee", "③ 하나만 달라집니다")}</Text>
    <Text fontSize="sm" mb={4}>{tt("a.339b5941", "①과 ②는 지금과")} <b>{tt("a.0bb75c14", "완전히 동일")}</b>{tt("a.28394db1", "합니다. 직접 구하신 작품의 비율만 조정됩니다.")}</Text>
    <Text fontSize="xs" color="neutral.400" mb={2}>{tt("a.50596b41", "지금")}</Text>
    <Bar laeebly={0} creatorLabel="크리에이터 100" soft />
    <Text color="brand.600" fontSize="lg" my={2}>↓</Text>
    <Text fontSize="xs" color="neutral.400" mb={2}>{tt("a.f616d13b", "2026년 10월 1일부터")}</Text>
    <Bar laeebly={40} laeeblyLabel="레이블리 40" creatorLabel="크리에이터 60" />
    <Box mt={5} borderTop="1px solid" borderColor="neutral.100">
      {[
        <MarkedText text={tt("a.c02ab4ba", "미션·챌린지·어워즈 등 **콘텐츠 성과와 무관한 활동 보상**은 전액 크리에이터님 몫입니다.")} />,
        <MarkedText text={tt("a.5d29b26e", "9월 30일까지 발생한 수익은 **종전 조건 그대로** 정산됩니다.")} />,
      ].map((t, i) => (
        <Flex key={i} py={3.5} borderBottom="1px solid" borderColor="neutral.100" fontSize="sm" gap={3}>
          <Text color="brand.600" fontWeight="800">✓</Text><Text>{t}</Text>
        </Flex>
      ))}
    </Box>
  </Box>
);

const PlatCard = ({ name, en, applied, rows, dashed }) => (
  <Box border="1px solid" borderColor="neutral.200" borderRadius="12px" overflow="hidden" mt={3}
    borderStyle={dashed ? "dashed" : "solid"} bg={dashed ? "neutral.50" : "white"}>
    <Flex align="center" gap={3} p={4} bg={dashed ? undefined : name === "네이버 클립" ? "#eaf8ee" : "#fdf6d4"}>
      <Box>
        <Text fontWeight="800" fontSize="sm">{name}</Text>
        <Text fontSize="xs" color="neutral.400">{en}</Text>
      </Box>
      <Badge ml="auto" colorScheme={applied ? "brand" : "gray"} fontSize="xs" px={3} py={1}>
        {applied ? "적용" : "적용 안 됨"}
      </Badge>
    </Flex>
    <Table size="sm" fontSize="xs">
      <Tbody>
        {rows.map(([k, v]) => (
          <Tr key={k}><Td color="neutral.400" w="90px" whiteSpace="nowrap">{k}</Td><Td>{v}</Td></Tr>
        ))}
      </Tbody>
    </Table>
  </Box>
);

const Step5 = () => (
  <Box>
    <StepTag>{tt("a.abe14c24", "적용 범위")}</StepTag>
    <Text fontSize="lg" fontWeight="800" mb={3}>{tt("a.258d9113", "이 두 곳에만 적용됩니다")}</Text>
    <Text fontSize="sm"><MarkedText text={tt("a.126d1e21", "네이버 클립과 카카오톡 숏폼에는 **동일하게**, 그 밖의 플랫폼에는 **적용되지 않습니다.**")} /></Text>
    <PlatCard name="네이버 클립" en="NAVER Clip" applied rows={[
      ["대상 수익", "클립 콘텐츠에서 발생한 수익"],
      ["산정 방식", "채널 수익 ÷ 채널 총조회수 × 내 영상 조회수"],
      ["제외", "해시태그 미션 · 월간 어워즈 · 클립 챌린지 보상"],
    ]} />
    <PlatCard name="카카오톡 숏폼" en="KakaoTalk 숏폼" applied rows={[
      ["대상 수익", "숏폼 콘텐츠에서 발생한 수익"],
      ["산정 방식", "네이버 클립과 동일 (조회수 안분)"],
      ["제외", "콘텐츠 성과와 무관한 활동 보상"],
    ]} />
    <PlatCard name="유튜브" en="YouTube" dashed rows={[
      ["수익 배분", "지금과 동일합니다. 이번 정책의 적용 대상이 아닙니다"],
      ["협업 계약", "그대로 유지됩니다"],
    ]} />
    <InfoNote>
      <b>{tt("a.60141d49", "채널도 대상이 정해져 있습니다.")}</b> {tt("a.49208c72", "레이블리의 지원으로 수익창출 자격을 얻은 채널에만 적용되며, 직접 통과하신 채널은 지금과 같습니다.")}
    </InfoNote>
  </Box>
);

const Step6 = ({ checked, setChecked, disabled }) => (
  <Box>
    <StepTag>{tt("a.561135d3", "일정")}</StepTag>
    <Text fontSize="lg" fontWeight="800" mb={3}>{tt("a.f5972038", "10월 1일 발생분부터 적용됩니다")}</Text>
    <Box borderTop="1px solid" borderColor="neutral.100">
      {[
        ["오늘", "안내를 시작합니다. 지금 정산에는 아무 영향이 없습니다."],
        ["9월 30일", "유예기간이 끝납니다. 그때까지 한 달 동안은 종전 방식이 그대로 유지됩니다. 이 기간에 게시 구성을 조정하실 수 있습니다."],
        ["10월 1일", "이날 이후 발생한 수익부터 새 배분이 적용됩니다."],
      ].map(([d, t], i) => (
        <Flex key={d} py={3.5} borderBottom="1px solid" borderColor="neutral.100" fontSize="sm" gap={4}>
          <Text w="70px" flexShrink={0} fontWeight="800" color={i ? "brand.600" : "neutral.800"}>{d}</Text>
          <Text>{t}</Text>
        </Flex>
      ))}
    </Box>
    <Box bg="brand.50" border="1px solid" borderColor="brand.200" borderRadius="10px" p={4} fontSize="sm" my={5}>
      {tt("a.9e982eeb", "동의하신 기록은")} <b>{tt("a.c4de9cc5", "설정 → 약관 및 정책")}</b>{tt("a.eeef4885", "에서 언제든 다시 확인하실 수 있고,")}{" "}
      <b>{tt("a.18f544e3", "9월 30일까지는 같은 곳에서 동의를 철회")}</b>{tt("a.7afee01c", "하실 수도 있습니다. 10월 1일 이후에는 철회하실 수 없습니다.")}
    </Box>
    <Box border="1px solid" borderColor="neutral.200" borderRadius="12px" p={4}>
      <Checkbox
        colorScheme="brand"
        isChecked={checked}
        isDisabled={disabled}
        onChange={(e) => setChecked(e.target.checked)}
        alignItems="flex-start"
        sx={{ ".chakra-checkbox__control": { mt: 1 } }}
      >
        <Text fontSize="sm">
          {tt("a.12d32590", "위 내용을 모두 확인했으며,")} <b>{tt("a.a0273d0b", "2026년 10월 1일 발생분부터")}</b> {tt("a.3651ba68", "네이버 클립·카카오톡 숏폼에 새 수익 배분이 적용되는 것에 동의합니다.")}
        </Text>
      </Checkbox>
    </Box>
  </Box>
);

const STEP_COUNT = 6;

const PolicyConsentModal = ({ isOpen, policy, userEmail, onDecision, onDefer }) => {
  const [step, setStep] = useState(0);
  const [readDone, setReadDone] = useState(false);
  const [checked, setChecked] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [saving, setSaving] = useState(false);
  const bodyRef = useRef(null);
  const shownLoggedRef = useRef(false);
  const toast = useToast();

  const impersonating = isImpersonating();
  const inGrace = isWithinGrace();
  const version = policy?.version || SHORTFORM_POLICY.version;

  // 노출 이력 — 열릴 때 1회 (§6-1)
  useEffect(() => {
    if (!isOpen || shownLoggedRef.current || !userEmail) return;
    shownLoggedRef.current = true;
    supabase.from("consent_prompt_log").insert({
      user_email: userEmail, policy_key: SHORTFORM_POLICY.key, version, action: "shown",
    }).then(() => {});
  }, [isOpen, userEmail, version]);

  useEffect(() => {
    if (!isOpen) { setStep(0); setChecked(false); setRejecting(false); shownLoggedRef.current = false; }
  }, [isOpen]);

  // Android 하드웨어 뒤로가기 차단 — 차단형이므로 리스너를 잡아 아무것도 하지 않는다
  useEffect(() => {
    if (!isOpen || !isNativePlatform()) return undefined;
    let handle;
    import("@capacitor/app").then(({ App }) => {
      handle = App.addListener("backButton", () => {});
    });
    return () => { if (handle) handle.then ? handle.then((h) => h.remove()) : handle.remove(); };
  }, [isOpen]);

  // 장별 스크롤 완료 게이트
  const checkScroll = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 40) setReadDone(true);
  }, []);

  useEffect(() => {
    setReadDone(false);
    const el = bodyRef.current;
    if (el) el.scrollTop = 0;
    // 내용이 화면 안에 다 들어오면 즉시 통과
    const t = setTimeout(checkScroll, 50);
    return () => clearTimeout(t);
  }, [step, isOpen, rejecting, checkScroll]);

  const record = async (agreed, method) => {
    setSaving(true);
    try {
      const documentHash = await hashDocumentText(canonicalPolicyText(SHORTFORM_POLICY));
      const { error } = await supabase.from("user_policy_consent").insert({
        user_email: userEmail,
        policy_key: SHORTFORM_POLICY.key,
        version,
        document_hash: documentHash,
        agreed,
        method,
      });
      if (error) throw error;
      toast({
        title: agreed ? tt("a.11fe8b79", "동의가 완료되었습니다.") : tt("a.ce7a86be", "거절이 접수되었습니다."),
        description: agreed
          ? tt("a.eab06332", "기록은 설정 → 약관 및 정책에서 확인하실 수 있습니다.")
          : tt("a.d6f5d37c", "레이블리 작품으로 만드신 게시물은 9월 30일까지 삭제해 주세요."),
        status: agreed ? "success" : "warning",
        duration: 6000,
      });
      onDecision(agreed);
    } catch (e) {
      toast({ title: tt("a.11133dc3", "처리하지 못했습니다."), description: e.message, status: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleDefer = async () => {
    try {
      await supabase.from("consent_prompt_log").insert({
        user_email: userEmail, policy_key: SHORTFORM_POLICY.key, version, action: "deferred",
      });
    } catch (e) { /* 유보 기록 실패는 흐름을 막지 않는다 */ }
    onDefer();
  };

  const steps = useMemo(() => [
    <Step1 key={1} />, <Step2 key={2} />, <Step3 key={3} />, <Step4 key={4} />, <Step5 key={5} />,
    <Step6 key={6} checked={checked} setChecked={setChecked} disabled={impersonating} />,
  ], [checked, impersonating]);

  const last = step === STEP_COUNT - 1;

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {}}
      closeOnOverlayClick={false}
      closeOnEsc={false}
      size="2xl"
      scrollBehavior="inside"
      isCentered
    >
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent borderRadius="16px" overflow="hidden" maxH="88dvh">
        <Box h="6px" bg={rejecting ? "#d96c3f" : TEAL} />
        {!rejecting ? (
          <>
            <ModalHeader pb={2}>
              <Text fontSize="xs" fontWeight="700" color="brand.600" mb={1}>{tt("a.96f0263b", "숏폼 수익 배분 정책 안내")}</Text>
              <Text fontSize="xl" fontWeight="800">{tt("a.8f3d74d7", "꼭 확인해 주세요")}</Text>
              <Flex gap={2} mt={3}>
                {Array.from({ length: STEP_COUNT }).map((_, i) => (
                  <Box key={i} flex={1} h="4px" borderRadius="2px"
                    bg={i < step ? "brand.200" : i === step ? TEAL : "neutral.200"} />
                ))}
              </Flex>
              <Text fontSize="sm" color="neutral.400" mt={2}>
                <b>{step + 1}</b> / {STEP_COUNT}
              </Text>
              {impersonating && (
                <Box bg="orange.50" border="1px solid" borderColor="orange.200" borderRadius="8px"
                  p={2.5} mt={2} fontSize="xs" color="orange.700">
                  {tt("a.3949fb91", "운영팀 화면 보기(임퍼소네이션) 중에는 동의·거절할 수 없습니다 — 본인 로그인 시에만 가능합니다.")}
                </Box>
              )}
            </ModalHeader>
            <ModalBody ref={bodyRef} onScroll={checkScroll} pt={2}>
              {steps[step]}
            </ModalBody>
            <ModalFooter gap={2} borderTop="1px solid" borderColor="neutral.100" flexWrap="wrap">
              <Button variant="outline" onClick={() => setStep((s) => s - 1)} isDisabled={step === 0}>
                {tt("a.cb1dc9f2", "이전")}
              </Button>
              {inGrace && (
                <Text fontSize="xs" color="neutral.400" mr="auto" display={{ base: "none", md: "block" }}>
                  {tt("a.afeb94ac", "유예기간 중에는 미루실 수 있습니다")}
                </Text>
              )}
              <Button variant="outline" colorScheme="orange" onClick={() => setRejecting(true)}
                isDisabled={impersonating || saving}>
                {tt("a.8feba42e", "거절")}
              </Button>
              {inGrace && (
                <Button variant="outline" onClick={handleDefer} isDisabled={saving}>{tt("a.a26db980", "나중에")}</Button>
              )}
              {!last ? (
                <Button colorScheme="brand" onClick={() => setStep((s) => s + 1)}
                  isDisabled={!readDone} title={readDone ? undefined : "끝까지 읽으시면 활성화됩니다"}>
                  {tt("a.fe687980", "다음")}
                </Button>
              ) : (
                <Button colorScheme="brand" px={7}
                  isDisabled={!readDone || !checked || impersonating}
                  isLoading={saving}
                  onClick={() => record(true, "popup")}>
                  {tt("a.1499ecbc", "동의하고 시작하기")}
                </Button>
              )}
            </ModalFooter>
          </>
        ) : (
          <>
            <ModalHeader>
              <Text fontSize="lg" fontWeight="800">{tt("a.d6642a0e", "거절하시면 게시물을 모두 삭제하셔야 합니다")}</Text>
            </ModalHeader>
            <ModalBody ref={bodyRef} onScroll={checkScroll}>
              <Box bg="#fdf1ea" border="1px solid" borderColor="#f3cdb8" borderRadius="12px" p={4} mb={4} fontSize="sm" color="#8a4a2d">
                <Text mb={2}>{tt("a.95edf0b7", "거절하시면 다음과 같이 처리됩니다.")}</Text>
                <Box pl={3}>
                  <Text><MarkedText text={tt("a.26025bdb", "· 레이블리 작품을 **더 이상 사용하실 수 없습니다.**")} /></Text>
                  <Text><MarkedText text={tt("a.1b10340d", "· 플랫폼에 제출된 공문을 **회수하며 효력이 중단됩니다.**")} /></Text>
                  <Text><MarkedText text={tt("a.451a6daf", "· 레이블리 작품으로 만드신 **게시물을 9월 30일까지 모두 삭제**하셔야 합니다.")} /></Text>
                </Box>
              </Box>
              <Text fontSize="sm" mb={3}>
                <b>{tt("a.c582d1b7", "네이버 클립·카카오톡 숏폼에만 해당됩니다. 유튜브 협업은 그대로 유지")}</b>{tt("a.0c2f58ad", "되고, 협업 계약이 해지되지도 않습니다. 언제든 다시 동의하시면 숏폼 작품 제공이 재개됩니다.")}
              </Text>
              <Text fontSize="sm">
                {tt("a.f9661365", "게시물 삭제 기한은")} <b>{tt("a.531eff1a", "9월 30일")}</b>{tt("a.44cc4db9", "입니다. 지금 정하지 않으셔도 되지만,")}{" "}
                <b>{tt("a.254febb3", "미루실 수 있는 기한도 9월 30일까지")}</b>{tt("a.fa621894", "라 늦게 정하실수록 삭제하실 시간이 줄어듭니다.")}
              </Text>
            </ModalBody>
            <ModalFooter gap={3}>
              <Button variant="outline" flex={1} onClick={() => setRejecting(false)} isDisabled={saving}>
                {tt("a.9576e83e", "돌아가기")}
              </Button>
              <Button flex={1} bg="#d96c3f" color="white" _hover={{ bg: "#c85e33" }}
                isLoading={saving} isDisabled={impersonating}
                onClick={() => record(false, "popup")}>
                {tt("a.5d0e5f73", "거절하겠습니다")}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default PolicyConsentModal;
